import { type JobStatus } from '@jobos/shared/types/job';
import { JOB_STATUS_CONFIG } from '@jobos/shared/constants/job-statuses';
import { cn, formatDate } from '@/lib/utils';
import { unknownStatusConfig } from './unknownStatus';

const EMERGENCY_CLASS = 'bg-red-100 text-red-800 border-red-500';

interface JobStatusBadgeProps {
  status: JobStatus;
  className?: string;
  noticeDate?: string | null;
  estimateSendDate?: string | null;
  startDate?: string | null;
  completedDate?: string | null;
}

function getRelevantDate(
  status: JobStatus,
  dates: Pick<
    JobStatusBadgeProps,
    'noticeDate' | 'estimateSendDate' | 'startDate' | 'completedDate'
  >
): string | null | undefined {
  switch (status) {
    case 'quote_requested':
      return dates.noticeDate;
    case 'estimate_sent':
      return dates.estimateSendDate;
    case 'awarded':
    case 'not_awarded':
      return dates.estimateSendDate;
    case 'in_progress':
      return dates.startDate;
    case 'completed':
      return dates.completedDate;
    default:
      return null;
  }
}

function isQuoteOverdue(status: JobStatus, noticeDate: string | null | undefined): boolean {
  if (status !== 'quote_requested' || !noticeDate) return false;
  const notice = new Date(noticeDate);
  const today = new Date();
  notice.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today.getTime() - notice.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays >= 10;
}

export function JobStatusBadge({
  status,
  className,
  noticeDate,
  estimateSendDate,
  startDate,
  completedDate,
}: JobStatusBadgeProps) {
  // ?? — the column is plain text, so an unrecognised value is possible.
  const config = JOB_STATUS_CONFIG[status] ?? unknownStatusConfig(status);
  const relevantDate = getRelevantDate(status, {
    noticeDate,
    estimateSendDate,
    startDate,
    completedDate,
  });
  const overdue = isQuoteOverdue(status, noticeDate);

  return (
    <div className="flex flex-col items-center">
      <span
        className={cn(
          'inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium border text-center min-w-[90px]',
          overdue ? EMERGENCY_CLASS : config.className,
          className
        )}
      >
        {config.label}
      </span>
      {relevantDate && (
        <span className="text-[10px] text-gray-500 mt-0.5">
          {formatDate(relevantDate)}
        </span>
      )}
    </div>
  );
}
